import {
  provide,
  inject,
  ref,
  onBeforeMount,
  onBeforeUnmount,
  type InjectionKey
} from 'vue'
import { emitter } from '@/plugins/emitter'
import { moveHistoryListItem } from '@/utils/format-edit/historyList'
import type {
  ShokurekishoFormatData,
  ShokurekishoImageItem
} from '@/composables/format-edit/useShokurekishoFormatData'

function createUuid(): string {
  const cryptoObj =
    window.crypto || (window as Window & { msCrypto?: Crypto }).msCrypto
  const template = '10000000-1000-4000-8000-100000000000'
  return template.replace(/[018]/g, (c) =>
    (
      Number(c) ^
      (cryptoObj!.getRandomValues(new Uint8Array(1))[0] &
        (15 >> (Number(c) / 4)))
    ).toString(16)
  )
}

type ImageColumns = Partial<Omit<ShokurekishoImageItem, 'uuid'>>

function ensureImageItems(
  formatData: ShokurekishoFormatData
): ShokurekishoImageItem[] {
  if (!formatData.imageItems) formatData.imageItems = []
  return formatData.imageItems
}

function createImageItem(columns: ImageColumns = {}): ShokurekishoImageItem {
  return {
    title: columns.title || '',
    tools: columns.tools || '',
    description: columns.description || '',
    mainImage: columns.mainImage || null,
    subImage1: columns.subImage1 || null,
    subImage2: columns.subImage2 || null,
    subImage3: columns.subImage3 || null,
    subImage4: columns.subImage4 || null,
    subImage5: columns.subImage5 || null,
    uuid: createUuid()
  }
}

export type ShokurekishoImageItems = ReturnType<
  typeof useShokurekishoImageItems
>

const SHOKUREKISHO_IMAGE_ITEMS_KEY: InjectionKey<ShokurekishoImageItems> =
  Symbol('shokurekishoImageItems')

export function useShokurekishoImageItems(formatData: ShokurekishoFormatData) {
  const activeImageItem = ref('')

  function addImageItem(index?: number | null, columns?: ImageColumns) {
    const list = ensureImageItems(formatData)
    const item = createImageItem(columns)
    if (typeof index === 'number') {
      list.splice(index, 0, item)
    } else {
      list.push(item)
    }
  }

  function deleteImageItem(index: number) {
    ensureImageItems(formatData).splice(index, 1)
  }

  function moveImageItem(params: { index: number; to: string }) {
    moveHistoryListItem(ensureImageItems(formatData), params)
  }

  function activateImageItem(id: string) {
    activeImageItem.value = id
  }

  function deactivateImageItem() {
    activeImageItem.value = ''
  }

  onBeforeMount(() => {
    ensureImageItems(formatData)
    emitter.on('deactivate-image-item', deactivateImageItem)
  })

  onBeforeUnmount(() => {
    emitter.off('deactivate-image-item', deactivateImageItem)
  })

  return {
    activeImageItem,
    addImageItem,
    deleteImageItem,
    moveImageItem,
    activateImageItem,
    deactivateImageItem
  }
}

export function provideShokurekishoImageItems(items: ShokurekishoImageItems) {
  provide(SHOKUREKISHO_IMAGE_ITEMS_KEY, items)
}

export function useProvidedShokurekishoImageItems(): ShokurekishoImageItems {
  const items = inject(SHOKUREKISHO_IMAGE_ITEMS_KEY)
  if (!items) {
    throw new Error(
      'useProvidedShokurekishoImageItems() must be used under ShokurekishoWithImageEdit'
    )
  }
  return items
}
